import {
  datadogRum,
  type PropagatorType,
  type RumInitConfiguration,
  type RumResourceEventDomainContext,
} from '@datadog/browser-rum';
import appEnv from 'app-env';
import { authManager } from '@/core/auth';
import {
  getWindowLocationEnvironment,
  isPreviewEnvironment,
  isProductionEnvironment,
} from '@/core/env/env.util';
import { logger } from '@/core/logger';

const DEFAULT_SITE = 'datadoghq.eu';
const DEFAULT_SERVICE = 'main-app';
const DEFAULT_SESSION_SAMPLE_RATE_PRODUCTION = 20;
const DEFAULT_SESSION_SAMPLE_RATE_NON_PRODUCTION = 100;
const TRACING_PROPAGATOR_TYPES: PropagatorType[] = ['tracecontext', 'datadog'];

type RumUser = {
  id: string;
  organization_id?: string;
};

const getAppVersion = () =>
  typeof __APP_VERSION__ === 'string' ? __APP_VERSION__ : 'unknown';

const getCommitSha = () =>
  typeof __APP_SHA__ === 'string' && __APP_SHA__ ? __APP_SHA__ : 'unknown';

const clampPercentage = (value: number) => Math.min(Math.max(value, 0), 100);

const getDefaultSessionSampleRate = () =>
  isProductionEnvironment()
    ? DEFAULT_SESSION_SAMPLE_RATE_PRODUCTION
    : DEFAULT_SESSION_SAMPLE_RATE_NON_PRODUCTION;

const getAllowedTracingUrls = (): NonNullable<
  RumInitConfiguration['allowedTracingUrls']
> => [
  {
    match: appEnv.GRAPHQL_API_ENDPOINT,
    propagatorTypes: TRACING_PROPAGATOR_TYPES,
  },
];

const getRumUser = (): RumUser | null => {
  const userId = authManager.idTokenParsed?.sub;
  if (!userId) return null;

  const organizationId = authManager.idTokenParsed?.organization_id;
  const resolvedOrganizationId = Array.isArray(organizationId)
    ? organizationId[0]
    : organizationId;

  return {
    id: userId,
    organization_id:
      typeof resolvedOrganizationId === 'string'
        ? resolvedOrganizationId
        : undefined,
  };
};

const enrichGraphqlResourceName: RumInitConfiguration['beforeSend'] = (
  event,
  context
) => {
  if (
    event.type === 'resource' &&
    event.resource.type === 'fetch' &&
    event.resource.url.includes('/graphql')
  ) {
    const fetchContext = context as RumResourceEventDomainContext | undefined;
    const body = fetchContext?.requestInit?.body;

    if (typeof body === 'string') {
      try {
        const parsed = JSON.parse(body) as { operationName?: string };
        if (parsed.operationName) {
          event.resource.url = `${event.resource.url} [${parsed.operationName}]`;
        }
      } catch (error) {
        logger.debug(
          'Could not parse GraphQL operation name from request body',
          {
            error: error as Error,
          }
        );
      }
    }
  }

  return true;
};

const buildRumConfig = (): RumInitConfiguration => {
  const applicationId = appEnv.RUM_APPLICATION_ID;
  const clientToken = appEnv.RUM_CLIENT_TOKEN ?? appEnv.LOGGING_CLIENT_TOKEN;

  if (!applicationId || !clientToken) {
    throw new Error('Datadog RUM configuration is incomplete');
  }

  const defaultSampleRate = getDefaultSessionSampleRate();

  return {
    allowedTracingUrls: getAllowedTracingUrls(),
    // Cypress dispatches untrusted (synthetic) events; without this, RUM
    // ignores e2e interactions and browser-test sessions cannot be linked.
    allowUntrustedEvents: !isProductionEnvironment(),
    applicationId,
    clientToken,
    defaultPrivacyLevel: 'mask-user-input',
    env: getWindowLocationEnvironment(),
    service: appEnv.RUM_SERVICE ?? DEFAULT_SERVICE,
    sessionReplaySampleRate: clampPercentage(
      appEnv.RUM_SESSION_REPLAY_SAMPLE_RATE ?? 0
    ),
    sessionSampleRate: clampPercentage(
      appEnv.RUM_SESSION_SAMPLE_RATE ?? defaultSampleRate
    ),
    site: appEnv.RUM_SITE ?? DEFAULT_SITE,
    traceContextInjection: 'sampled',
    traceSampleRate: clampPercentage(
      appEnv.RUM_TRACE_SAMPLE_RATE ?? defaultSampleRate
    ),
    trackLongTasks: true,
    trackResources: true,
    trackUserInteractions: true,
    trackViewsManually: true,
    version: getAppVersion(),
    beforeSend: enrichGraphqlResourceName,
  };
};

const isInitialized = () => {
  try {
    return !!datadogRum.getInitConfiguration();
  } catch {
    return false;
  }
};

const safeRumCall = (fn: () => void, context: string) => {
  if (!isInitialized()) return;
  try {
    fn();
  } catch (error) {
    logger.error(`Datadog RUM error: ${context}`, { error: error as Error });
  }
};

export const startView = (name: string) =>
  safeRumCall(() => datadogRum.startView({ name }), 'startView');

export const addTiming = (name: string) =>
  safeRumCall(() => datadogRum.addTiming(name), 'addTiming');

export const addError = (error: Error, context: object) =>
  safeRumCall(() => datadogRum.addError(error, context), 'addError');

export const syncRumUserContext = () => {
  const user = getRumUser();
  if (!user) {
    logger.warn('RUM user context could not be set: no user ID available');
    return;
  }
  safeRumCall(() => datadogRum.setUser(user), 'setUser');
};

export const initializeRum = () => {
  if (isPreviewEnvironment()) return;
  if (appEnv.RUM_ENABLED !== true) return;

  if (!appEnv.RUM_APPLICATION_ID) {
    logger.warn('Datadog RUM is enabled but RUM_APPLICATION_ID is missing');
    return;
  }

  try {
    if (!isInitialized()) {
      datadogRum.init(buildRumConfig());
    }
  } catch (error) {
    logger.error('Failed to initialize Datadog RUM SDK', {
      error: error as Error,
    });
    return;
  }

  safeRumCall(() => {
    datadogRum.setGlobalContextProperty('app', 'main');
    datadogRum.setGlobalContextProperty('app_version', getAppVersion());
    datadogRum.setGlobalContextProperty('commit_sha', getCommitSha());
  }, 'setGlobalContext');

  syncRumUserContext();
};
